/**
 * MH-23 Theo dõi phiên phát (mục 6.7 đặc tả giao diện, Bảng 6.2) — dùng chung
 * cho lịch phát, phát ngay, phát khẩn cấp và phát trực tiếp.
 *
 * Máy chủ (`PhienPhatTheoDoiService.baNhom`) chỉ trả BA nhóm thiết bị (đã xác
 * nhận / chờ xác nhận / lỗi), không đủ năm nhóm của Bảng 6.2 ("đang phát" và
 * "không khả dụng" cần dữ liệu trạng thái thiết bị theo thời gian thực, khác
 * nguồn với `phat_log` — ghi ở NOTES-A0.md mục Z2-B4). Cột lý do lỗi LUÔN hiện
 * mã lỗi và diễn giải, không để trống, đúng yêu cầu bắt buộc của mục 6.7.
 *
 * Chu kỳ làm mới: mặc định 6 giây (đủ điều kiện "không dưới 5 giây" của chương
 * 12) trừ khi nơi gọi (MH-20 phát khẩn cấp) truyền `chuKyMs` nhỏ hơn — MH-20 là
 * màn hình DUY NHẤT được phép làm mới dưới 5 giây.
 */
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { phatSongApi, type BaNhomPhienPhat } from '../api/phat-song-api';
import { dungTaiDuLieu, dungTuLamMoi } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';

const CHU_KY_MAC_DINH_MS = 6_000;

export interface MH23Props {
  phienId?: string;
  api?: Pick<typeof phatSongApi, 'theoDoiPhien'>;
  /** Chu kỳ làm mới; chỉ MH-20 được truyền giá trị dưới 5.000 ms. */
  chuKyMs?: number;
}

export function MH23TheoDoiPhien({ phienId: phienIdProp, api, chuKyMs = CHU_KY_MAC_DINH_MS }: MH23Props): ReactNode {
  const goi = api ?? phatSongApi;
  const { phienId: phienIdDuongDan } = useParams<{ phienId: string }>();
  const phienId = phienIdProp ?? phienIdDuongDan ?? '';
  const [dangChonDong] = useState(false);

  const kq = dungTaiDuLieu<BaNhomPhienPhat>({
    tai: (signal) => goi.theoDoiPhien(phienId, signal),
    laRong: (d) => d.soThietBiUocTinh === 0,
    dangLoc: false,
    phuThuoc: [phienId],
    tat: !phienId,
  });

  const tuLamMoi = dungTuLamMoi(kq.taiLai, {
    chuKyMs,
    dangChonDong,
    dangMoNgan: false,
  });

  return (
    <div className="man-hinh" data-man-hinh="MH-23" data-chu-ky-lam-moi-ms={chuKyMs}>
      <h1>{vi.phatSong.theoDoiTieuDe}</h1>
      {chuKyMs < 5000 ? <p>{vi.phatSong.duyNhatDuoi5s}</p> : null}
      <p data-vung="trang-thai-tu-lam-moi">
        {tuLamMoi.dangTamDung ? vi.bang.tamDungLamMoi : dienVao(vi.phatSong.dangCapNhat, { giay: Math.round(chuKyMs / 1000) })}
      </p>

      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={5} soCot={3} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.phatSong.rongMoTa} /> : null}

      {kq.trangThai === 'co-du-lieu' && kq.duLieu ? (
        <div data-vung="ba-nhom-phien-phat">
          <p data-vung="tong-so-thiet-bi">
            {vi.phatSong.tongSoThietBi}: {kq.duLieu.soThietBiUocTinh}
          </p>

          <section aria-label={vi.phatSong.nhomDaXacNhan} data-nhom="da-xac-nhan">
            <h2>
              {vi.phatSong.nhomDaXacNhan} ({kq.duLieu.daXacNhan.soLuong})
            </h2>
            <ul>{(kq.duLieu.daXacNhan.danhSach ?? []).map((c) => <li key={c}>{c}</li>)}</ul>
          </section>

          <section aria-label={vi.phatSong.nhomChoXacNhan} data-nhom="cho-xac-nhan">
            <h2>
              {vi.phatSong.nhomChoXacNhan} ({kq.duLieu.choXacNhan.soLuong})
            </h2>
            <ul>{(kq.duLieu.choXacNhan.danhSach ?? []).map((c) => <li key={c}>{c}</li>)}</ul>
          </section>

          <section aria-label={vi.phatSong.nhomLoi} data-nhom="loi">
            <h2>
              {vi.phatSong.nhomLoi} ({kq.duLieu.loi.soLuong})
            </h2>
            <table className="bang">
              <thead>
                <tr>
                  <th scope="col">Cụm loa</th>
                  <th scope="col">Mã lỗi</th>
                  <th scope="col">Diễn giải</th>
                </tr>
              </thead>
              <tbody>
                {kq.duLieu.loi.danhSach.map((l) => (
                  <tr key={l.cumLoaId}>
                    <td>{l.cumLoaId}</td>
                    <td>{l.maLoi}</td>
                    <td>{l.thongBao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      ) : null}
    </div>
  );
}
